import React, { useCallback, useContext, useEffect, useState } from "react";
import i18next from "i18next";
import { Box, Collapse, Dialog, DialogContent, DialogTitle, IconButton, Tooltip, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { useHistory, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import SendIcon from "@mui/icons-material/Send";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { BRAND, FONT, RADIUS, SHADOW, useLightStyles, MEDIA } from "./lightTheme";
import { Context } from "states/Store";
import { getContextInstance, getContextInstanceThirdParty, getContextInstanceThirdPartyAttributes, archiveThirdParty, reactivateThirdParty, archiveContextInstance, reactivateContextInstance, updateContextInstance } from "services/edge/ContexteService";
import {
  getAllNoteContextInstance,
  createNoteContextInstance,
  deleteNoteContextInstance,
} from "services/edge/NoteService";
import { getDossiersByCriteria } from "services/edge/DossierService";
import { getAccountUsers } from "services/edge/UserService";
import { getApiErrorMessageCode } from "services/common/ApiUtils";
import { ERROR, SUCCESS } from "services/common/Constants";
import { RoleBasedAccessControlContext } from "services/common/RolesUtils";
import { PrivilegeEnum } from "services/common/RolesUtils";
import ROUTES, { getUrlWithAccountId } from "config/Routes";
import LightLoader from "./LightLoader";
import LightNotesList from "./components/LightNotesList";
import DossierAccordion from "./components/DossierAccordion";
import LightEditableAttributes from "./LightEditableAttributes";
import useMentionAutocomplete from "./hooks/useMentionAutocomplete";
import StopConfirmDialog from "./components/StopConfirmDialog";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { formatDt } from "./utils/lightUtils";

// ─── Styles ───────────────────────────────────────────────────────────────────
const useStyles = makeStyles()(() => ({
  // ── Notes ──
  noteInput: { display: "flex", gap: "0.5rem", alignItems: "center" },
  mentionInputWrap: { flex: 1, position: "relative" },
  noteTextarea: {
    width: "100%", boxSizing: "border-box", borderRadius: RADIUS.sm,
    border: `1.5px solid ${BRAND.border}`,
    padding: "0.5rem 0.65rem", fontFamily: FONT.family,
    ...FONT.body, resize: "none", outline: "none",
    transition: "border-color 0.15s",
    "&:focus": { borderColor: BRAND.primary },
    backgroundColor: BRAND.white, color: BRAND.textMain,
    lineHeight: 1.5, minHeight: "44px",
  },
  noteSendBtn: {
    height: "44px", width: "44px", borderRadius: RADIUS.sm,
    border: "none", backgroundColor: BRAND.primary,
    color: BRAND.white, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0, transition: "opacity 0.15s",
    "&:hover": { opacity: 0.88 },
    "&:disabled": { opacity: 0.35, cursor: "not-allowed" },
  },
  noteItem: {
    padding: "0.5rem 0.75rem", borderRadius: RADIUS.sm,
    backgroundColor: BRAND.white, border: `1px solid ${BRAND.border}`,
    display: "flex", flexDirection: "column", gap: "0.25rem",
  },
  noteHeader: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  noteAuthor: { ...FONT.body, fontWeight: 700, color: BRAND.textMain, fontFamily: FONT.family },
  noteDate: { ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family },
  noteContent: {
    ...FONT.body, color: BRAND.textMain, fontFamily: FONT.family,
    lineHeight: 1.5, whiteSpace: "pre-wrap",
  },
  noteDeleteBtn: {
    padding: "2px 4px", borderRadius: RADIUS.sm, border: "none",
    background: "transparent", cursor: "pointer",
    color: BRAND.textMuted, display: "flex", alignItems: "center",
    transition: "color 0.15s",
    "&:hover": { color: "#E84343" },
  },
  mentionAvatar: {
    width: "22px", height: "22px", borderRadius: "50%",
    backgroundColor: BRAND.primary, color: BRAND.white,
    display: "flex", alignItems: "center", justifyContent: "center",
    ...FONT.tiny, fontWeight: 700, flexShrink: 0,
  },
  mentionDropdown: {
    position: "absolute", zIndex: 200,
    top: "100%", left: 0, right: 0, marginTop: "4px",
    backgroundColor: BRAND.white, border: `1.5px solid ${BRAND.border}`,
    borderRadius: RADIUS.sm, boxShadow: SHADOW.strong,
    overflow: "hidden", maxHeight: "150px", overflowY: "auto",
  },
  mentionItem: {
    display: "flex", alignItems: "center", gap: "0.5rem",
    padding: "0.4rem 0.65rem", cursor: "pointer",
    transition: "background 0.1s",
    "&:hover": { backgroundColor: BRAND.background },
  },
  mentionName: { ...FONT.body, fontWeight: 500, color: BRAND.textMain, fontFamily: FONT.family },

  // ── Attribute row ──
  attrRow: {
    display: "flex", alignItems: "flex-start", gap: "0.75rem",
    padding: "0.6rem 0", borderBottom: `1px solid ${BRAND.border}`,
    "&:last-child": { borderBottom: "none" },
  },
  attrLabel: {
    ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family,
    flex: "0 0 200px", paddingTop: "1px",
  },
  attrValue: {
    ...FONT.body, fontWeight: 600, color: BRAND.textMain, fontFamily: FONT.family, flex: 1,
  },

  // ── Tiers accordion ──
  tpAccordion: {
    borderBottom: `1px solid ${BRAND.border}`,
    "&:last-child": { borderBottom: "none" },
  },
  tpHeader: {
    display: "flex", alignItems: "center", gap: "0.75rem",
    padding: "0.85rem 1.25rem", cursor: "pointer",
    transition: "background 0.12s",
    "&:hover": { backgroundColor: BRAND.background },
    [MEDIA.smDown]: { padding: "0.75rem 1rem", gap: "0.6rem" },
  },
  // Action buttons hidden on mobile to free room for supplier name
  tpHeaderAction: {
    [MEDIA.smDown]: { display: "none !important" },
  },
  tpIcon: {
    width: "34px", height: "34px", borderRadius: RADIUS.md, backgroundColor: BRAND.background,
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  tpInfo: { flex: 1, minWidth: 0 },
  tpName: {
    fontSize: "0.875rem", fontWeight: 600, color: BRAND.textMain,
    fontFamily: FONT.family, whiteSpace: "nowrap", overflow: "hidden",
    textOverflow: "ellipsis",
  },
  tpSub: { ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family },
  tpChevron: { fontSize: "18px", color: BRAND.textMuted, transition: "transform 0.25s", flexShrink: 0 },
  tpBody: {
    borderTop: `1px solid ${BRAND.border}`,
    backgroundColor: BRAND.background,
    padding: "0.75rem 1.25rem",
    display: "flex", flexDirection: "column", gap: "0.5rem",
  },
  dossierList: { display: "flex", flexDirection: "column", gap: "0.5rem" },
}));

// ─── ContextTierAccordion ─────────────────────────────────────────────────────
function ContextTierAccordion({ tp, contextId, isContextArchived, canArchive, onArchived }) {
  const { classes: s } = useStyles();
  const { classes: light } = useLightStyles();
  const [state, dispatch] = useContext(Context);
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dossiers, setDossiers] = useState([]);
  const [tiersAttrs, setTiersAttrs] = useState([]);
  const [editTpAttrsOpen, setEditTpAttrsOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");
  const [archiving, setArchiving] = useState(false);

  const isTpArchived = tp.isArchived || tp.archived;

  // Reset dossiers when archived status changes (after archive/reactivate)
  useEffect(() => {
    setLoaded(false);
    setDossiers([]);
  }, [isTpArchived]);

  const handleArchiveTp = async () => {
    setArchiving(true);
    try {
      await archiveThirdParty(state.account.id, tp.id, { motif: archiveReason.trim() }, dispatch);
      dispatch({ type: "ALERT", alert: { type: SUCCESS, msg: i18next.t("light.context.archiveTierSuccess") } });
      setArchiveDialogOpen(false);
      setArchiveReason("");
      onArchived?.();
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
    } finally {
      setArchiving(false);
    }
  };

  const handleReactivateTp = async () => {
    try {
      await reactivateThirdParty(state.account.id, tp.id, dispatch);
      dispatch({ type: "ALERT", alert: { type: SUCCESS, msg: i18next.t("light.context.reactivateTierSuccess") } });
      onArchived?.();
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
    }
  };

  const refreshDossiers = async () => {
    const base = `page=0&pageSize=50&withAttributes=true&thirdpartyId=${tp.compteFoId}&ctxThirdparty=${tp.id}`;
    try {
      const dossiersRes = await getDossiersByCriteria(state.account.id, `${base}&monitored=true`, dispatch);
      setDossiers(dossiersRes?.content ?? (Array.isArray(dossiersRes) ? dossiersRes : []));
    } catch (_) {}
  };

  // Fetch attributes only when accordion is opened
  const loadTiersAttrs = () => {
    if (tiersAttrs.length > 0) return;
    getContextInstanceThirdPartyAttributes(state.account.id, tp.id, contextId, dispatch)
      .then((r) => setTiersAttrs(r?.tiersAttributes ?? []))
      .catch(() => {});
  };

  const handleToggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) loadTiersAttrs();
    if (next && !loaded) {
      setLoading(true);
      try {
        const base = `page=0&pageSize=50&withAttributes=true&thirdpartyId=${tp.compteFoId}&ctxThirdparty=${tp.id}`;
        if (isTpArchived) {
          // Fetch both active and archived dossiers for archived tiers
          const [activeRes, archivedRes] = await Promise.all([
            getDossiersByCriteria(state.account.id, `${base}&monitored=true`, dispatch),
            getDossiersByCriteria(state.account.id, `${base}&monitored=false`, dispatch),
          ]);
          const active = activeRes?.content ?? (Array.isArray(activeRes) ? activeRes : []);
          const archived = archivedRes?.content ?? (Array.isArray(archivedRes) ? archivedRes : []);
          setDossiers([...active, ...archived]);
        } else {
          const dossiersRes = await getDossiersByCriteria(state.account.id, `${base}&monitored=true`, dispatch);
          setDossiers(dossiersRes?.content ?? (Array.isArray(dossiersRes) ? dossiersRes : []));
        }
        setLoaded(true);
      } catch (_) {
        setDossiers([]);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Box className={s.tpAccordion}>
      {/* Header */}
      <Box className={s.tpHeader} onClick={handleToggle}>
        <Box className={s.tpIcon}>
          <BusinessOutlinedIcon style={{ fontSize: "17px", color: BRAND.primary }} />
        </Box>
        <Box className={s.tpInfo}>
          <Typography className={s.tpName}>{tp.name}</Typography>
          {/* Attributs inline sous le nom */}
          {(tp.siret || tp.siren || tp.identifier) && (
            <Typography className={s.tpSub}>{tp.siret || tp.siren || tp.identifier}</Typography>
          )}
        </Box>
        {loaded && dossiers.length > 0 && (
          <Box style={{ backgroundColor: BRAND.background, border: `1px solid ${BRAND.border}`, borderRadius: "20px", padding: "1px 8px", ...FONT.tiny, fontWeight: 700, color: BRAND.textMuted, fontFamily: FONT.family, flexShrink: 0 }}>
            {dossiers.length}
          </Box>
        )}
        {isTpArchived && (
          <span style={{ fontSize: "0.65rem", fontWeight: 600, fontFamily: FONT.family, color: "#DC2626", backgroundColor: "#FEE2E2", padding: "0.15rem 0.5rem", borderRadius: RADIUS.sm, whiteSpace: "nowrap", flexShrink: 0 }}>
            {i18next.t("light.context.archivedSingular")}
          </span>
        )}
        <Tooltip title={i18next.t("light.contexts.supplierDetail")} placement="top" disableInteractive>
          <Box
            component="a"
            href={getUrlWithAccountId(state.account.id, `light/thirdparties/${tp.compteFoId ?? tp.id}`)}
            onClick={(e) => { e.stopPropagation(); if (e.metaKey || e.ctrlKey) return; e.preventDefault(); history.push({ pathname: getUrlWithAccountId(state.account.id, `light/thirdparties/${tp.compteFoId ?? tp.id}`), state: { fromContextId: contextId } }); }}
            className={s.tpHeaderAction}
            style={{ display: "flex", alignItems: "center", gap: "0.25rem", ...FONT.meta, fontWeight: 600, color: BRAND.primary, fontFamily: FONT.family, textDecoration: "none", padding: "4px 8px", borderRadius: RADIUS.sm, border: `1px solid ${BRAND.border}`, backgroundColor: BRAND.white, whiteSpace: "nowrap", flexShrink: 0 }}
          >
            <BusinessOutlinedIcon style={{ fontSize: "13px" }} />
            {i18next.t("light.contexts.supplierDetail")}
          </Box>
        </Tooltip>
        {canArchive && !isContextArchived && (
          isTpArchived ? (
            <Tooltip title={i18next.t("light.context.reactivateTier")} placement="top" disableInteractive>
              <button
                onClick={(e) => { e.stopPropagation(); handleReactivateTp(); }}
                className={s.tpHeaderAction}
                style={{ display: "flex", alignItems: "center", gap: "0.25rem", ...FONT.meta, fontWeight: 600, color: BRAND.primary, fontFamily: FONT.family, padding: "4px 8px", borderRadius: RADIUS.sm, border: `1px solid ${BRAND.border}`, backgroundColor: BRAND.white, whiteSpace: "nowrap", flexShrink: 0, cursor: "pointer", transition: "opacity 0.15s" }}
              >
                <PlayCircleOutlineIcon style={{ fontSize: "13px" }} />
                {i18next.t("light.context.reactivateTier")}
              </button>
            </Tooltip>
          ) : (
            <Tooltip title={i18next.t("light.context.archiveTier")} placement="top" disableInteractive>
              <button
                onClick={(e) => { e.stopPropagation(); setArchiveDialogOpen(true); }}
                className={s.tpHeaderAction}
                style={{ display: "flex", alignItems: "center", gap: "0.25rem", ...FONT.meta, fontWeight: 600, color: BRAND.textMuted, fontFamily: FONT.family, padding: "4px 8px", borderRadius: RADIUS.sm, border: `1px solid ${BRAND.border}`, backgroundColor: BRAND.white, whiteSpace: "nowrap", flexShrink: 0, cursor: "pointer", transition: "color 0.15s" }}
                onMouseEnter={(e) => { e.currentTarget.style.color = "#DC2626"; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = BRAND.textMuted; }}
              >
                <StopCircleOutlinedIcon style={{ fontSize: "13px" }} />
                {i18next.t("light.context.archiveTier")}
              </button>
            </Tooltip>
          )
        )}
        <ExpandMoreIcon className={s.tpChevron} style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }} />
      </Box>

      {/* Body */}
      <Collapse in={open}>
        <Box className={s.tpBody}>
          {/* Attributs tiers du contexte */}
          {(() => {
            const grouped = {};
            tiersAttrs.forEach((a) => {
              const vals = Array.isArray(a.values) ? a.values : (a.value != null ? [String(a.value)] : []);
              if (vals.length === 0 || vals[0] === "") return;
              if (!grouped[a.label]) grouped[a.label] = { ...a, values: [...vals] };
              else grouped[a.label].values.push(...vals);
            });
            const entries = Object.values(grouped);
            return (
              <Box style={{ paddingBottom: "0.75rem", borderBottom: `1px solid ${BRAND.border}`, marginBottom: "0.75rem" }}>
                <Box style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.3rem" }}>
                  <span style={{ ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family }}>{i18next.t("light.detail.attributes")}</span>
                  {!isTpArchived && (
                    <button
                      onClick={() => setEditTpAttrsOpen(true)}
                      style={{ border: "none", background: "transparent", cursor: "pointer", color: BRAND.textMuted, padding: "2px", borderRadius: RADIUS.sm, transition: "color 0.15s", display: "flex", alignItems: "center" }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = BRAND.primary; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = BRAND.textMuted; }}
                    >
                      <EditOutlinedIcon style={{ fontSize: "14px" }} />
                    </button>
                  )}
                </Box>
                {entries.length === 0 ? (
                  <span style={{ ...FONT.small, color: BRAND.textMuted, fontFamily: FONT.family, fontStyle: "italic" }}>{i18next.t("light.detail.noAttribute")}</span>
                ) : (
                  <Box style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
                    {entries.map((a) => (
                      <Tooltip key={a.label} title={a.values.length > 1 ? <>{a.values.map((v, i) => <div key={i}>{v}</div>)}</> : `${a.label} : ${a.values[0]}`} placement="top" enterDelay={200} disableInteractive>
                        <Box className={light.attrTag}>
                          <span className={light.attrTagLabel}>{a.label}</span>
                          <span className={light.attrTagValue}>{a.values.length > 1 ? `${a.values[0]} +${a.values.length - 1}` : a.values[0]}</span>
                        </Box>
                      </Tooltip>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })()}

          {/* Dossiers */}
          {loading ? (
            <LightLoader size="sm" />
          ) : dossiers.length === 0 ? (
            <Typography style={{ ...FONT.body, color: BRAND.textMuted, fontFamily: FONT.family, fontStyle: "italic", padding: "0.25rem 0" }}>
              {i18next.t("light.contexts.noDossiers")}
            </Typography>
          ) : (
            <Box className={s.dossierList}>
              {dossiers.map((d) => (
                <DossierAccordion
                  key={d.dossierId}
                  dossier={d}
                  accountId={state.account.id}
                  dispatch={dispatch}
                  readOnly={isTpArchived}
                  fromContextId={contextId}
                  onStopped={isTpArchived ? undefined : () => refreshDossiers()}
                />
              ))}
            </Box>
          )}
        </Box>
      </Collapse>

      {/* ── Dialog archivage tiers ── */}
      <StopConfirmDialog
        open={archiveDialogOpen}
        onClose={() => { setArchiveDialogOpen(false); setArchiveReason(""); }}
        onConfirm={handleArchiveTp}
        loading={archiving}
        title={i18next.t("light.context.archiveTier")}
        reasonLabel={i18next.t("light.context.archiveReasonLabel")}
        reasonPlaceholder={i18next.t("light.context.archiveReasonPlaceholder")}
        reason={archiveReason}
        onReasonChange={setArchiveReason}
        cancelLabel={i18next.t("light.common.cancel")}
        confirmLabel={i18next.t("light.context.archiveTier")}
        confirmingLabel={i18next.t("light.common.loading")}
      >
        {i18next.t("light.context.archiveTierConfirm")} <strong>{tp.name}</strong> ?
      </StopConfirmDialog>

      {/* ── Dialog attributs tiers du contexte ── */}
      <Dialog
        open={editTpAttrsOpen}
        onClose={() => setEditTpAttrsOpen(false)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { borderRadius: "16px", fontFamily: "Inter, sans-serif", maxHeight: "85vh" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "Inter", fontSize: "1rem", fontWeight: 700, color: BRAND.white, backgroundColor: BRAND.primary, pb: 1.5, flexShrink: 0 }}>
          <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <BusinessOutlinedIcon style={{ fontSize: "18px", color: "#54CC54" }} />
            {i18next.t("light.detail.attributes")} — {tp.name}
          </Box>
          <IconButton size="small" onClick={() => setEditTpAttrsOpen(false)} sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: BRAND.white } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 3, overflowY: "auto" }}>
          <LightEditableAttributes
            accountId={state.account?.id}
            entityId={contextId}
            bondType="context_third_party"
            contextThirdpartyId={tp.id}
            initialValues={tiersAttrs}
            onSaved={() => {
              getContextInstanceThirdPartyAttributes(state.account.id, tp.id, contextId, dispatch)
                .then((r) => setTiersAttrs(r?.tiersAttributes ?? []))
                .catch(() => {});
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function LightContextDetail() {
  const { classes: s } = useStyles();
  const { classes: light } = useLightStyles();
  const [state, dispatch] = useContext(Context);
  const history = useHistory();
  const { contextId } = useParams();

  const [ctx, setCtx] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [notes, setNotes] = useState([]);
  const [accountUsers, setAccountUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [tierSearch, setTierSearch] = useState("");
  const [tiersPage, setTiersPage] = useState(0);
  const [editAttrsOpen, setEditAttrsOpen] = useState(false);
  const [tiersOpen, setTiersOpen] = useState(true);
  const TIERS_PAGE_SIZE = 10;
  const [notesOpen, setNotesOpen] = useState(false);

  // Archive context
  const { hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);
  const canArchiveContext = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_ARCHIVE_CONTEXT);
  const [archiveCtxDialogOpen, setArchiveCtxDialogOpen] = useState(false);
  const [editDescOpen, setEditDescOpen] = useState(false);
  const [editDescValue, setEditDescValue] = useState("");
  const [archiveCtxReason, setArchiveCtxReason] = useState("");
  const [archivingCtx, setArchivingCtx] = useState(false);

  const mention = useMentionAutocomplete(accountUsers, noteText, setNoteText);

  const load = useCallback(async () => {
    if (!state.account?.id || !contextId) return;
    setLoading(true);
    try {
      const instance = await getContextInstance(state.account.id, contextId, dispatch);
      setCtx(instance);
      await Promise.allSettled([
        getContextInstanceThirdParty(state.account.id, contextId, dispatch)
          .then((r) => setTiers(Array.isArray(r) ? r : (r?.content ?? []))),
        getAllNoteContextInstance(state.account.id, contextId, dispatch)
          .then((r) => setNotes(r?.content ?? [])),
        getAccountUsers(state.account.id, dispatch)
          .then((r) => setAccountUsers(r?.content ?? [])),
      ]);
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
    } finally {
      setLoading(false);
    }
  }, [state.account?.id, contextId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // ── Notes handlers ──
  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      const mentionRegexp = /@([\w\u00C0-\u017E]+ [\w\u00C0-\u017E]+)/g;
      const matched = [...noteText.matchAll(mentionRegexp)].map((m) => m[1]);
      const filteredUsers = mention.selectedMentions
        .filter((m) => matched.includes(m.nomPrenom))
        .map((m) => m.userOid);
      await createNoteContextInstance(state.account.id, contextId, {
        content: noteText.trim(),
        selectedUsersIds: filteredUsers,
      }, dispatch);
      setNoteText("");
      mention.resetMentions();
      const res = await getAllNoteContextInstance(state.account.id, contextId, dispatch);
      setNotes(res?.content ?? []);
    } catch (_) { /* silent */ }
    setSavingNote(false);
  };

  const handleDeleteNote = async (noteOId) => {
    try {
      await deleteNoteContextInstance(state.account.id, contextId, noteOId, dispatch);
      setNotes((prev) => prev.filter((n) => n.noteOId !== noteOId));
    } catch (_) { /* silent */ }
  };

  const refreshTiers = useCallback(() => {
    if (!state.account?.id || !contextId) return;
    getContextInstanceThirdParty(state.account.id, contextId, dispatch)
      .then((r) => setTiers(Array.isArray(r) ? r : (r?.content ?? [])))
      .catch(() => {});
  }, [state.account?.id, contextId, dispatch]);

  const handleArchiveContext = async () => {
    setArchivingCtx(true);
    try {
      await archiveContextInstance(state.account.id, contextId, { motif: archiveCtxReason.trim() }, dispatch);
      dispatch({ type: "ALERT", alert: { type: SUCCESS, msg: i18next.t("light.context.archiveContextSuccess") } });
      setArchiveCtxDialogOpen(false);
      setArchiveCtxReason("");
      load();
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
    } finally {
      setArchivingCtx(false);
    }
  };

  const handleReactivateContext = async () => {
    try {
      await reactivateContextInstance(state.account.id, contextId, dispatch);
      dispatch({ type: "ALERT", alert: { type: SUCCESS, msg: i18next.t("light.context.reactivateContextSuccess") } });
      load();
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
    }
  };

  // ── Render ──
  if (loading) return <LightLoader overlay />;
  if (!ctx) return null;

  const isArchived = ctx.isArchived || ctx.archived;

  return (
    <Box className={light.pageRoot}>
      {/* Banner */}
      <Box className={light.pageHeader} style={isArchived ? { backgroundColor: "#1D2939", backgroundImage: "none" } : undefined}>
        <Box className={light.pageHeaderInner}>
          <Box
            component="a"
            href={getUrlWithAccountId(state.account.id, ROUTES.LIGHT_CONTEXTS)}
            className={light.backBtnWhite}
            onClick={(e) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); history.push(getUrlWithAccountId(state.account.id, ROUTES.LIGHT_CONTEXTS)); }}
            style={{ textDecoration: "none" }}
          >
            <ArrowBackIcon style={{ fontSize: "16px" }} />
            {ctx.contextModelName ?? i18next.t("light.contexts.title")}
          </Box>

          {isArchived && (
            <Box style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", backgroundColor: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: RADIUS.md, padding: "0.4rem 1rem", marginBottom: "0.25rem" }}>
              <StopCircleOutlinedIcon style={{ fontSize: "15px", color: "#FCA5A5" }} />
              <Typography style={{ ...FONT.body, fontWeight: 700, color: "#FCA5A5", fontFamily: FONT.family }}>
                {i18next.t("light.thirdparties.archived")}
              </Typography>
            </Box>
          )}

          <Box style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", minWidth: 0 }}>
            <Box style={{ display: "flex", alignItems: "center", gap: "0.75rem", minWidth: 0 }}>
              <Box style={{ width: "40px", height: "40px", borderRadius: RADIUS.md, backgroundColor: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <AccountTreeOutlinedIcon style={{ fontSize: "22px", color: BRAND.white }} />
              </Box>
              <Typography className={light.pageTitleWhite} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                {ctx.reference || `#${ctx.id}`}
              </Typography>
            </Box>
            {canArchiveContext && (
              isArchived ? (
                <button
                  onClick={handleReactivateContext}
                  style={{ display: "flex", alignItems: "center", gap: "0.35rem", background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: RADIUS.sm, padding: "0.35rem 0.75rem", color: "rgba(255,255,255,0.85)", cursor: "pointer", fontFamily: FONT.family, ...FONT.small, fontWeight: 600, transition: "background 0.15s", flexShrink: 0 }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.25)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.15)"; }}
                >
                  <PlayCircleOutlineIcon style={{ fontSize: "15px" }} />
                  {i18next.t("light.context.reactivateContext")}
              </button>
            ) : (
              <button
                onClick={() => setArchiveCtxDialogOpen(true)}
                style={{ display: "flex", alignItems: "center", gap: "0.35rem", background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: RADIUS.sm, padding: "0.35rem 0.75rem", color: "rgba(255,255,255,0.85)", cursor: "pointer", fontFamily: FONT.family, ...FONT.small, fontWeight: 600, transition: "background 0.15s", flexShrink: 0 }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.25)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.15)"; }}
              >
                <StopCircleOutlinedIcon style={{ fontSize: "15px" }} />
                {i18next.t("light.context.archiveContext")}
              </button>
            )
          )}
          </Box>

        </Box>
      </Box>

      <Box className={light.pageInner} style={{ marginTop: "-0.75rem" }}>
        {/* ── Info card ── */}
        <Box className={light.sectionCard} style={{ marginBottom: "1.25rem" }}>
          <Box style={{ padding: "1.25rem 1.5rem", display: "flex", flexDirection: "column", gap: "0.9rem" }}>
            <Box>
              <Box style={{ display: "flex", alignItems: "center", gap: "0.35rem", marginBottom: "0.2rem" }}>
                <Typography style={{ ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family }}>
                  {i18next.t("light.contexts.description")}
                </Typography>
                {!isArchived && (
                  <EditOutlinedIcon
                    onClick={() => { setEditDescValue(ctx.description || ""); setEditDescOpen(true); }}
                    style={{ fontSize: "13px", color: BRAND.textMuted, cursor: "pointer", transition: "color 0.15s" }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = BRAND.primary; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = BRAND.textMuted; }}
                  />
                )}
              </Box>
              <Typography style={{ ...FONT.body, color: ctx.description ? BRAND.textMain : BRAND.textMuted, fontFamily: FONT.family, lineHeight: 1.5, fontStyle: ctx.description ? "normal" : "italic", whiteSpace: "pre-wrap" }}>
                {ctx.description || i18next.t("light.common.noDescription")}
              </Typography>
            </Box>
            {(true) && (
              <Box style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem 2.5rem" }}>
                <Box>
                  <Typography style={{ ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.2rem" }}>
                    {i18next.t("light.contexts.colExpiration")}
                  </Typography>
                  <Box style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                    <Typography style={{ ...FONT.body, fontWeight: 600, color: BRAND.textMain, fontFamily: FONT.family }}>
                      {ctx.expirationDate ? formatDt(ctx.expirationDate) : "—"}
                    </Typography>
                    {!isArchived && (
                      <Box style={{ position: "relative", display: "inline-flex" }}>
                        <input
                          type="date"
                          value={ctx.expirationDate ? ctx.expirationDate.substring(0, 10) : ""}
                          onChange={async (e) => {
                            const val = e.target.value || null;
                            try {
                              await updateContextInstance(state.account.id, contextId, { ...ctx, expirationDate: val || null }, dispatch);
                              setCtx((prev) => ({ ...prev, expirationDate: val }));
                            } catch (_) {}
                          }}
                          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" }}
                        />
                        <EditOutlinedIcon
                          style={{ fontSize: "13px", color: BRAND.textMuted, cursor: "pointer", transition: "color 0.15s" }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = BRAND.primary; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = BRAND.textMuted; }}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
                {ctx.creationDate && (
                  <Box>
                    <Typography style={{ ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.2rem" }}>
                      {i18next.t("light.contexts.colCreation")}
                    </Typography>
                    <Typography style={{ ...FONT.body, fontWeight: 600, color: BRAND.textMain, fontFamily: FONT.family }}>
                      {formatDt(ctx.creationDate)}
                    </Typography>
                  </Box>
                )}
                {ctx.createdBy && (
                  <Box>
                    <Typography style={{ ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.2rem" }}>
                      {i18next.t("light.contexts.colCreatedBy")}
                    </Typography>
                    <Typography style={{ ...FONT.body, fontWeight: 600, color: BRAND.textMain, fontFamily: FONT.family }}>
                      {typeof ctx.createdBy === "object"
                        ? `${ctx.createdBy.prenom || ""} ${ctx.createdBy.nom || ""}`.trim() || ctx.createdBy.email
                        : ctx.createdBy}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* ── Attributes inline ── */}
            <Box style={{ paddingTop: "0.6rem", borderTop: `1px solid ${BRAND.border}` }}>
              <Box style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.4rem" }}>
                <span style={{ ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family }}>{i18next.t("light.detail.attributes")}</span>
                <button
                  onClick={() => setEditAttrsOpen(true)}
                  style={{ border: "none", background: "transparent", cursor: "pointer", color: BRAND.textMuted, padding: "2px", borderRadius: RADIUS.sm, transition: "color 0.15s", display: "flex", alignItems: "center" }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = BRAND.primary; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = BRAND.textMuted; }}
                >
                  <EditOutlinedIcon style={{ fontSize: "14px" }} />
                </button>
              </Box>
              {(() => {
                // Group attributes by label to merge multi-value entries
                const grouped = {};
                (ctx.contextAttributes ?? []).forEach((a) => {
                  const vals = Array.isArray(a.values) ? a.values : (a.value != null ? [String(a.value)] : []);
                  if (vals.length === 0 || vals[0] === "") return;
                  if (!grouped[a.label]) grouped[a.label] = { ...a, values: [...vals] };
                  else grouped[a.label].values.push(...vals);
                });
                const entries = Object.values(grouped);
                return entries.length === 0 ? (
                  <span style={{ ...FONT.small, color: BRAND.textMuted, fontFamily: FONT.family, fontStyle: "italic" }}>{i18next.t("light.detail.noAttribute")}</span>
                ) : (
                  <Box style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
                    {entries.map((a) => (
                      <Tooltip key={a.label} title={a.values.length > 1 ? <>{a.values.map((v, i) => <div key={i}>{v}</div>)}</> : `${a.label} : ${a.values[0]}`} placement="top" enterDelay={200} disableInteractive>
                        <Box className={light.attrTag}>
                          <span className={light.attrTagLabel}>{a.label}</span>
                          <span className={light.attrTagValue}>{a.values.length > 1 ? `${a.values[0]} +${a.values.length - 1}` : a.values[0]}</span>
                        </Box>
                      </Tooltip>
                    ))}
                  </Box>
                );
              })()}
            </Box>
          </Box>
        </Box>

        {/* ── Tiers ── */}
        {(() => {
          const sortedTiers = [...tiers].sort((a, b) => {
            const aArch = a.isArchived || a.archived ? 1 : 0;
            const bArch = b.isArchived || b.archived ? 1 : 0;
            if (aArch !== bArch) return aArch - bArch; // actifs d'abord
            if (aArch && bArch) {
              // archivés triés par date d'archivage desc
              const da = a.stoppingDate || a.archiveDate || "";
              const db = b.stoppingDate || b.archiveDate || "";
              return db.localeCompare(da);
            }
            return 0;
          });
          const filteredTiers = tierSearch
            ? sortedTiers.filter((tp) => tp.name?.toLowerCase().includes(tierSearch.toLowerCase()))
            : sortedTiers;
          const totalPages = Math.ceil(filteredTiers.length / TIERS_PAGE_SIZE);
          const page = Math.min(tiersPage, Math.max(0, totalPages - 1));
          const pageTiers = filteredTiers.slice(page * TIERS_PAGE_SIZE, (page + 1) * TIERS_PAGE_SIZE);
          return (
            <Box className={light.sectionCard}>
              <Box className={light.sectionHead} style={{ cursor: "pointer", userSelect: "none" }}>
                <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem", flex: 1 }} onClick={() => setTiersOpen((o) => !o)}>
                  <Typography className={light.sectionTitle}>{i18next.t("light.contexts.tiers")}</Typography>
                  <Box className={light.sectionCount}>{filteredTiers.length}{tierSearch ? `/${tiers.length}` : ""}</Box>
                </Box>
                <button
                  className={light.btnPrimary}
                  style={{ padding: "0.35rem 0.75rem", fontSize: "0.75rem", display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    history.push({
                      pathname: getUrlWithAccountId(state.account.id, ROUTES.LIGHT_LAUNCH),
                      state: {
                        preselectedContext: {
                          instanceId: parseInt(contextId, 10),
                          isNew: false,
                          reference: ctx?.reference ?? "",
                          modelName: ctx?.contextModelName ?? "",
                        },
                      },
                    });
                  }}
                >
                  {i18next.t("light.contexts.addSupplier")}
                </button>
                <input
                  placeholder={i18next.t("light.contexts.searchSupplier")}
                  value={tierSearch}
                  onChange={(e) => { setTierSearch(e.target.value); setTiersPage(0); if (!tiersOpen) setTiersOpen(true); }}
                  onClick={(e) => e.stopPropagation()}
                  style={{ padding: "0.25rem 0.65rem", borderRadius: RADIUS.sm, border: `1.5px solid ${BRAND.border}`, fontFamily: FONT.family, fontSize: "0.78rem", color: BRAND.textMain, outline: "none", width: "200px" }}
                />
                <Box onClick={() => setTiersOpen((o) => !o)}>
                  <ExpandMoreIcon style={{ fontSize: "18px", color: BRAND.textMuted, transform: tiersOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", marginLeft: "0.25rem" }} />
                </Box>
              </Box>
              <Collapse in={tiersOpen} timeout={250} unmountOnExit>
                {filteredTiers.length === 0 ? (
                  <Box style={{ padding: "1.5rem", textAlign: "center", ...FONT.body, color: BRAND.textMuted, fontFamily: FONT.family, fontStyle: "italic" }}>
                    {i18next.t("light.common.noResult")}
                  </Box>
                ) : (
                  <>
                    {pageTiers.map((tp) => (
                      <ContextTierAccordion key={tp.id} tp={tp} contextId={contextId} isContextArchived={isArchived} canArchive={canArchiveContext} onArchived={refreshTiers} />
                    ))}
                    {totalPages > 1 && (
                      <Box style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem", padding: "0.75rem 1rem", borderTop: `1px solid ${BRAND.border}` }}>
                        <button
                          onClick={() => setTiersPage((p) => Math.max(0, p - 1))}
                          disabled={page === 0}
                          style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "28px", height: "28px", borderRadius: RADIUS.sm, border: `1px solid ${BRAND.border}`, background: BRAND.white, cursor: page === 0 ? "default" : "pointer", color: page === 0 ? BRAND.textMuted : BRAND.textMain, opacity: page === 0 ? 0.4 : 1, transition: "all 0.15s" }}
                        >
                          <ExpandMoreIcon style={{ fontSize: "16px", transform: "rotate(90deg)" }} />
                        </button>
                        <Typography style={{ ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family }}>
                          {page + 1} / {totalPages}
                        </Typography>
                        <button
                          onClick={() => setTiersPage((p) => Math.min(totalPages - 1, p + 1))}
                          disabled={page === totalPages - 1}
                          style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "28px", height: "28px", borderRadius: RADIUS.sm, border: `1px solid ${BRAND.border}`, background: BRAND.white, cursor: page === totalPages - 1 ? "default" : "pointer", color: page === totalPages - 1 ? BRAND.textMuted : BRAND.textMain, opacity: page === totalPages - 1 ? 0.4 : 1, transition: "all 0.15s" }}
                        >
                          <ExpandMoreIcon style={{ fontSize: "16px", transform: "rotate(-90deg)" }} />
                        </button>
                      </Box>
                    )}
                  </>
                )}
              </Collapse>
            </Box>
          );
        })()}

        {/* ── Notes ── */}
        <Box className={light.sectionCard}>
          <Box className={light.sectionHead} onClick={() => setNotesOpen((o) => !o)} style={{ cursor: "pointer", userSelect: "none" }}>
            <ChatBubbleOutlineIcon style={{ fontSize: "14px", color: BRAND.textMuted }} />
            <Typography className={light.sectionTitle}>{i18next.t("light.detail.notes")}</Typography>
            {notes.length > 0 && <Box className={light.sectionCount}>{notes.length}</Box>}
            <Box style={{ marginLeft: "auto" }}>
              <ExpandMoreIcon style={{ fontSize: "18px", color: BRAND.textMuted, transform: notesOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }} />
            </Box>
          </Box>
          <Collapse in={notesOpen} timeout={250} unmountOnExit>
            <Box style={{ padding: "1rem 1.25rem", borderTop: `1px solid ${BRAND.border}`, display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              <Box className={s.noteInput}>
                <Box className={s.mentionInputWrap}>
                  <textarea
                    className={s.noteTextarea}
                    placeholder={i18next.t("light.detail.noNote")}
                    rows={2}
                    value={noteText}
                    ref={mention.textareaRef}
                    onChange={mention.handleTextChange}
                    onKeyDown={(e) => mention.handleKeyDown(e, handleAddNote)}
                    style={{ width: "100%", boxSizing: "border-box" }}
                  />
                  {mention.filteredUsers?.length > 0 && (
                    <Box className={s.mentionDropdown} ref={mention.dropdownRef}>
                      {mention.filteredUsers.map((u) => (
                        <Box key={u.userId ?? u.id} className={s.mentionItem} onClick={() => mention.insertMention(u)}>
                          <Box className={s.mentionAvatar}>
                            {`${u.firstname ?? ""} ${u.name ?? ""}`.trim().split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)}
                          </Box>
                          <Typography className={s.mentionName}>{`${u.firstname ?? ""} ${u.name ?? ""}`.trim()}</Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
                <Tooltip title={i18next.t("light.detail.send")} placement="top" disableInteractive>
                  <button className={s.noteSendBtn} onClick={handleAddNote} disabled={savingNote || !noteText.trim()}>
                    <SendIcon style={{ fontSize: "18px" }} />
                  </button>
                </Tooltip>
              </Box>
              {notes.length === 0 ? (
                <Typography style={{ ...FONT.body, color: BRAND.textMuted, fontFamily: FONT.family, fontStyle: "italic" }}>
                  {i18next.t("light.detail.noNoteYet")}
                </Typography>
              ) : (
                <LightNotesList notes={notes} onDelete={handleDeleteNote} canDelete classes={s} />
              )}
            </Box>
          </Collapse>
        </Box>
      </Box>

      {/* ── Dialog attributs contexte ── */}
      <Dialog
        open={editAttrsOpen}
        onClose={() => setEditAttrsOpen(false)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { borderRadius: "16px", fontFamily: "Inter, sans-serif", maxHeight: "85vh" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "Inter", fontSize: "1rem", fontWeight: 700, color: BRAND.white, backgroundColor: BRAND.primary, pb: 1.5, flexShrink: 0 }}>
          <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <AccountTreeOutlinedIcon style={{ fontSize: "18px", color: "#54CC54" }} />
            {i18next.t("light.contexts.attributes")} — {ctx.label ?? ctx.name}
          </Box>
          <IconButton size="small" onClick={() => setEditAttrsOpen(false)} sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: BRAND.white } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 3, overflowY: "auto" }}>
          <LightEditableAttributes
            accountId={state.account?.id}
            entityId={ctx.id}
            bondType="context"
            initialValues={ctx.contextAttributes ?? []}
            onSaved={() => {
              getContextInstance(state.account.id, contextId, dispatch).then(setCtx).catch(() => {});
            }}
          />
        </DialogContent>
      </Dialog>

      {/* ── Dialog archivage contexte ── */}
      <StopConfirmDialog
        open={archiveCtxDialogOpen}
        onClose={() => { setArchiveCtxDialogOpen(false); setArchiveCtxReason(""); }}
        onConfirm={handleArchiveContext}
        loading={archivingCtx}
        title={i18next.t("light.context.archiveContext")}
        reasonLabel={i18next.t("light.context.archiveReasonLabel")}
        reasonPlaceholder={i18next.t("light.context.archiveReasonPlaceholder")}
        reason={archiveCtxReason}
        onReasonChange={setArchiveCtxReason}
        cancelLabel={i18next.t("light.common.cancel")}
        confirmLabel={i18next.t("light.context.archiveContext")}
        confirmingLabel={i18next.t("light.common.loading")}
      >
        {i18next.t("light.context.archiveContextConfirm")} <strong>{ctx.reference || `#${ctx.id}`}</strong> ?
      </StopConfirmDialog>

      {/* ── Dialog édition description ── */}
      <Dialog
        open={editDescOpen}
        onClose={() => setEditDescOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: RADIUS.lg, fontFamily: FONT.family, overflow: "hidden" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "Inter", fontSize: "1rem", fontWeight: 700, color: BRAND.white, backgroundColor: BRAND.primary, pb: 1.5 }}>
          <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <EditOutlinedIcon style={{ fontSize: "18px" }} />
            {i18next.t("light.contexts.description")}
          </Box>
          <IconButton size="small" onClick={() => setEditDescOpen(false)} sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: BRAND.white } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 3 }}>
          <textarea
            value={editDescValue}
            onChange={(e) => setEditDescValue(e.target.value)}
            rows={4}
            style={{ width: "100%", boxSizing: "border-box", borderRadius: RADIUS.sm, border: `1.5px solid ${BRAND.border}`, padding: "0.6rem 0.75rem", fontFamily: FONT.family, ...FONT.body, resize: "vertical", outline: "none", color: BRAND.textMain, lineHeight: 1.5, transition: "border-color 0.15s", marginTop: "0.5rem" }}
            onFocus={(e) => { e.target.style.borderColor = BRAND.primary; }}
            onBlur={(e) => { e.target.style.borderColor = BRAND.border; }}
          />
          <Box style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "1rem" }}>
            <button className={light.btnSecondary} style={{ padding: "0.5rem 1.25rem" }} onClick={() => setEditDescOpen(false)}>
              {i18next.t("light.common.cancel")}
            </button>
            <button className={light.btnPrimary} style={{ padding: "0.5rem 1.25rem" }} onClick={async () => {
              try {
                await updateContextInstance(state.account.id, contextId, {
                  ...ctx,
                  description: editDescValue.trim(),
                }, dispatch);
                setCtx((prev) => ({ ...prev, description: editDescValue.trim() }));
                setEditDescOpen(false);
              } catch (_) {}
            }}>
              {i18next.t("light.common.save")}
            </button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
